import { memo, useState, useCallback, useEffect } from 'react';

import { Loader } from 'components/Loader';

import { Category } from 'router/routes';

import { Company } from 'types';
import { getCompanyIdAndCategory } from 'utils';

import { ListItem } from './components/ListItem';

import styles from './styles.module.scss';

type Props = {
  loading: boolean;
  currentPathname: string;
  companies: Company[];
  onSelectedCategoryChange: (companyId: number, category: Category) => void;
};

export const List: React.FC<Props> = memo(function List({
  loading,
  currentPathname,
  companies,
  onSelectedCategoryChange,
}) {
  const [expandedCompany, setExpandedCompany] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  useEffect(() => {
    const companyIdAndCategory = getCompanyIdAndCategory(currentPathname);
    if (companyIdAndCategory) {
      setExpandedCompany(companyIdAndCategory.companyId);
      setSelectedCompany(companyIdAndCategory.companyId);
      setSelectedCategory(companyIdAndCategory.category);
    } else {
      setSelectedCompany(null);
      setSelectedCategory(null);
    }
  }, [currentPathname]);

  const handleCompanyClick = useCallback((companyId: number) => {
    setExpandedCompany((prevValue) => (prevValue === companyId ? null : companyId));
  }, []);

  const handleCategoryClick = useCallback(
    (companyId: number, category: Category) => {
      onSelectedCategoryChange(companyId, category);
    },
    [onSelectedCategoryChange],
  );

  return (
    <div className={styles.container}>
      {companies.map((company) => (
        <ListItem
          key={company.id}
          company={company}
          expanded={expandedCompany === company.id}
          companySelected={selectedCompany === company.id}
          selectedCategory={selectedCategory}
          onCompanyClick={handleCompanyClick}
          onCategoryClick={handleCategoryClick}
        />
      ))}
      <Loader loading={loading} />
    </div>
  );
});
